import {css, StyleSheet} from 'aphrodite';
import React, {useCallback} from 'react';

import {pluralize} from 'BenchlingUtil';
import SearchParamDropdown from 'search/SearchParamDropdown';
import Tooltip from 'ui-library/bs/Tooltip';
import {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARK, GRAY_LIGHT} from 'ui-library/Colors';
import usePrevious from 'ui-library/hooks/usePrevious';

interface SecondaryFilterDropdownSkeletonProps {
  buttonSize: ButtonSize;
  hasFilterErrors: boolean;
  secondaryFilterNodes: Array<JSX.Element>; // Array of wrappers around SecondaryFilterFormSkeleton
  onAddFilter: () => void;
  isOpen: boolean;
  onToggle: (isOpen: boolean) => void;
  disabled?: boolean;
  quickFiltersSection?: JSX.Element;
  dropLeft?: boolean;
}

export default function SecondaryFilterDropdownSkeleton(
  props: SecondaryFilterDropdownSkeletonProps
): JSX.Element {
  const prevCount = usePrevious(props.secondaryFilterNodes.length);
  const scrollableRef = useCallback(
    (scrollable: HTMLElement | null) => {
      // Scroll to the bottom when the user adds a new filter.
      if (scrollable && prevCount && prevCount < props.secondaryFilterNodes.length) {
        scrollable.scrollTop = scrollable.scrollHeight - scrollable.clientHeight;
      }
    },
    [prevCount, props.secondaryFilterNodes]
  );

  return (
    <Tooltip title="Some filters are invalid and were ignored." disabled={!props.hasFilterErrors}>
      <SearchParamDropdown
        title={
          props.secondaryFilterNodes.length > 0
            ? `${props.secondaryFilterNodes.length} ${pluralize(props.secondaryFilterNodes.length, 'filter')}`
            : 'Filters'
        }
        icon="filter-menu"
        addText="Add filters"
        buttonSize={props.buttonSize}
        buttonStyle={(() => {
          if (props.hasFilterErrors) {
            return ButtonStyle.WARNING_LIGHT;
          } else if (props.secondaryFilterNodes.length > 0) {
            return ButtonStyle.TERTIARY_BLUE_POWDER;
          } else {
            return ButtonStyle.TERTIARY_GRAY;
          }
        })()}
        onAddParam={props.onAddFilter}
        isOpen={props.isOpen}
        onToggle={props.onToggle}
        buttonDisabled={props.disabled}
        dropLeft={props.dropLeft}
      >
        <div className={css(styles.filterDropdownContainer)}>
          {props.quickFiltersSection}
          {props.secondaryFilterNodes.length > 0 ? (
            <div ref={scrollableRef} className="scrollable">
              {props.secondaryFilterNodes.map((secondaryFilterNode, index) => (
                <div key={index} className={css(styles.filterNodeContainer)}>
                  <div>{secondaryFilterNode}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className={css(styles.text)}>No active filters</div>
          )}
        </div>
      </SearchParamDropdown>
    </Tooltip>
  );
}

const styles = StyleSheet.create({
  filterDropdownContainer: {
    width: 400,
  },
  filterNodeContainer: {
    borderBottomColor: GRAY_LIGHT,
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
    padding: 10,
  },
  text: {
    color: GRAY_DARK,
    padding: '14px 12px',
  },
});
